import * as React from "react"
import { Row } from "react-grid-system"
import Subtitle from "../../atoms/typography/Subtitle"
import styled from "styled-components"
import { MediaQuery } from "../../../utils/design-helpers"
import TextInput from "../../molecules/TextInput"
import H3 from "../../atoms/typography/H3"
import { theme } from "../../../components/theme"

import { useOtpLogin } from "../../../hooks/useOtpLogin"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import TextButton from "../../atoms/buttons/TextButton"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"
import Body from "../../atoms/typography/Body"

const Content = styled.div`
  padding: 2rem 3rem;
  width: 40vw;

  ${MediaQuery.SM} {
    width: 40vw;
  }

  ${MediaQuery.XS} {
    width: 90vw;
    padding: 1rem;
  }
`

interface Props {
  onClickGoBack: () => void
  onClickBackupToken: () => void
  onCloseModal: () => void
  userHasBackupTokens: boolean
  redirectUrl?: string
}

const TwoFactorAuthenicationCard: React.FunctionComponent<Props> = ({
  onClickGoBack,
  onClickBackupToken,
  onCloseModal,
  userHasBackupTokens,
  redirectUrl,
}) => {
  const [token, setToken] = React.useState("")

  const onCompleted2faLogIn = (data: any) => {
    if (data?.totpLogin?.success) {
      onCloseModal()
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    }
  }
  const onError2faLogIn = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "Error in 2fa login")
  }
  const { otpLogin, data, loading } = useOtpLogin({
    onCompleted: (data: any) => onCompleted2faLogIn(data),
    onError: onError2faLogIn,
  })
  const onSubmitToken = async (event: any) => {
    event.preventDefault()

    try {
      await otpLogin({
        variables: {
          totpToken: token,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "Error in 2fa login")
    }
  }
  const onClickBackOutOf2fa = async () => {
    try {
      await otpLogin({
        variables: {
          goBack: true,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "Error clicking back out of 2fa")
    }
  }

  const onGoBack = () => {
    onClickBackOutOf2fa()
    onClickGoBack()
  }

  return (
    <Row justify="center">
      <Content>
        <HorizontalSpacer spacing={1} />
        <Subtitle textAlign="center">Two factor authenication</Subtitle>
        <HorizontalSpacer spacing={3} />
        <form onSubmit={(e) => onSubmitToken(e)}>
          <TextInput
            textFieldTitle="Verification code"
            onChange={(e) => setToken(e.target.value)}
            type="number"
            autofocus
          />
          <HorizontalSpacer spacing={2} />
          <Body>
            Enter the code from your two-factor authenticator app.{" "}
            {userHasBackupTokens && (
              <>If you've lost your device, you can click below to enter one of your recovery codes.</>
            )}
          </Body>
          <HorizontalSpacer spacing={3} />
          {userHasBackupTokens && (
            <>
              <Row justify="center">
                <TextButton title="Use back up token" onClick={onClickBackupToken} />
              </Row>
              <HorizontalSpacer spacing={2} />
            </>
          )}

          {data?.totpLogin?.errors?.length > 0 && (
            <div>
              <HorizontalSpacer spacing={1} />
              <H3 color={theme.colors.red}>{data.totpLogin.errors}</H3>
            </div>
          )}
          <HorizontalSpacer spacing={3} />
          <Row justify="center">
            <PrimaryButton title="Cancel" onClick={onGoBack} type="button" />

            <Spacer spacing={2} />
            {loading ? (
              <LoadingSpinner />
            ) : (
              <SecondaryButton title="Verify code" disabled={token.length < 1} type="submit" />
            )}
          </Row>
        </form>
        <HorizontalSpacer spacing={1} />
      </Content>
    </Row>
  )
}

export default TwoFactorAuthenicationCard
